import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { PublicAmbient, KineticHeading } from "@/components/public/PublicVisuals";
import { useCountUp } from "@/hooks/useCountUp";

const FACTORS = [
  { key: "demand", label: "Evidence of real demand", hint: "Have real people asked for this or paid for something similar?" },
  { key: "skill", label: "Your relevant skill / experience", hint: "How well does this match what you can already credibly do?" },
  { key: "differentiation", label: "Differentiation", hint: "Is there a clear reason to choose you over what already exists?" },
  { key: "competition", label: "Room in the market", hint: "Is the market un-crowded enough to enter (higher = more room)?" },
  { key: "reach", label: "Ability to reach customers", hint: "Can you actually get in front of buyers without a huge budget?" },
  { key: "time", label: "Time you can commit weekly", hint: "Realistically, how much focused time can you give this?" },
  { key: "budget", label: "Runway / upfront budget", hint: "Can you fund the first few months without pressure?" },
  { key: "selling", label: "Willingness to sell & market", hint: "Are you prepared to do the outreach and selling it needs?" },
];
const OPTS = ["None / very low", "Low", "Moderate", "Strong", "Very strong"];

function verdict(score) {
  if (score >= 72) return { label: "Worth validating properly", tone: "text-donkey-green", stroke: "#4ADE80", body: "On your own answers this looks promising — but your opinion isn't evidence. The next step is to test the riskiest assumptions with real people before you build." };
  if (score >= 48) return { label: "Mixed — validate before committing", tone: "text-donkey-amber", stroke: "#F5A623", body: "There's something here, but also real gaps. Don't quit anything yet. Pin down the weak factors below with evidence before spending money or time." };
  return { label: "Reconsider or reshape", tone: "text-donkey-red", stroke: "#E5504A", body: "On your own answers this is shaky. That's useful to know now, cheaply. Either reshape the idea around your strengths, or gather evidence that changes these answers." };
}

// Live instrument gauge — the arc + colour respond instantly as the user drags any input.
function Gauge({ score, stroke, label }) {
  const C = 2 * Math.PI * 52;
  return (
    <div className="relative w-[150px] h-[150px]">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
        <motion.circle
          cx="60" cy="60" r="52" fill="none" stroke={stroke} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={C}
          animate={{ strokeDashoffset: C - (C * score) / 100 }}
          transition={{ type: "spring", stiffness: 120, damping: 20 }}
          style={{ filter: `drop-shadow(0 0 6px ${stroke}88)` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading font-extrabold text-4xl text-donkey-warm" data-testid="viability-live-score">{score}</span>
        <span className="text-[10px] uppercase tracking-wider text-donkey-grey">{label}</span>
      </div>
    </div>
  );
}

export default function ViabilityCalculator() {
  const [vals, setVals] = useState(Object.fromEntries(FACTORS.map((f) => [f.key, 2])));
  const [done, setDone] = useState(false);
  const total = Object.values(vals).reduce((a, b) => a + b, 0);
  const score = Math.round((total / (FACTORS.length * 4)) * 100);
  const v = verdict(score);
  const weakest = [...FACTORS].sort((a, b) => vals[a.key] - vals[b.key]).slice(0, 3);
  const reduce = useReducedMotion();
  const shownScore = useCountUp(score, done, { duration: 1200, reduce });

  return (
    <div className="relative min-h-screen bg-donkey-bg grain text-donkey-warm overflow-hidden">
      <PublicAmbient intensity={0.4} />
      <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8 py-12">
        <KineticHeading text="Free Business Idea Viability Check" className="text-4xl sm:text-5xl mb-3" />
        <p className="text-donkey-grey text-base mb-2 max-w-2xl">A quick, honest gut-check on a business idea across the eight things that actually decide whether it's worth pursuing. No sign-up, no email, no hype.</p>
        <p className="text-xs text-donkey-grey mb-10 max-w-2xl">This reflects only what <em>you</em> tell it — it's a starting point, not evidence. The full, evidence-led Donkey's Verdict digs into real market data.</p>

        <div className="grid lg:grid-cols-[1fr_300px] gap-8 items-start">
          {/* instrument controls */}
          <div className="space-y-4 order-2 lg:order-1" data-testid="viability-form">
            {FACTORS.map((f) => (
              <div key={f.key} className="card-donkey rounded-xl p-4">
                <div className="flex items-baseline justify-between gap-3">
                  <label htmlFor={f.key} className="font-heading font-semibold text-donkey-warm text-sm">{f.label}</label>
                  <span className="text-xs text-donkey-orange" data-testid={`val-${f.key}`}>{OPTS[vals[f.key]]}</span>
                </div>
                <p className="text-xs text-donkey-grey mt-0.5 mb-2">{f.hint}</p>
                <input id={f.key} type="range" min="0" max="4" step="1" value={vals[f.key]}
                  onChange={(e) => setVals({ ...vals, [f.key]: Number(e.target.value) })}
                  aria-label={f.label} className="w-full accent-donkey-orange" data-testid={`slider-${f.key}`} />
              </div>
            ))}
          </div>

          {/* live readout — responds to every change */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-6">
            <div className="card-donkey rounded-2xl p-6 flex flex-col items-center">
              <p className="text-[11px] uppercase tracking-[0.2em] text-donkey-orange mb-4">Live reading</p>
              <Gauge score={score} stroke={v.stroke} label="viability" />
              <span className={`mt-4 text-center font-heading font-semibold ${v.tone}`}>{v.label}</span>
              {/* live factor signals */}
              <div className="w-full mt-5 space-y-1.5">
                {FACTORS.map((f) => (
                  <div key={f.key} className="flex items-center gap-2">
                    <span className="text-[10px] text-donkey-grey w-[42%] shrink-0 truncate">{f.label}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-white/8 overflow-hidden">
                      <motion.div className="h-full rounded-full" style={{ background: v.stroke }}
                        animate={{ width: `${(vals[f.key] / 4) * 100}%` }} transition={{ type: "spring", stiffness: 160, damping: 22 }} />
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={() => setDone(true)} className="bg-donkey-orange text-donkey-black font-semibold mt-6 w-full" data-testid="viability-calculate">See my honest gut-check</Button>
            </div>
          </div>
        </div>

        {done && (
          <motion.div initial={reduce ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="relative overflow-hidden card-donkey rounded-2xl p-6 mt-8" data-testid="viability-result">
            {!reduce && <span className="brand-lightsweep" aria-hidden />}
            <div className="relative flex items-end gap-4 mb-3">
              <span className="font-heading font-extrabold text-5xl" data-testid="viability-score">{shownScore}</span>
              <span className="text-donkey-grey mb-1">/ 100</span>
              <span className={`ml-auto font-heading font-semibold text-lg ${v.tone}`} data-testid="viability-verdict">{v.label}</span>
            </div>
            <p className="text-sm text-donkey-warm/90 mb-4">{v.body}</p>
            <p className="text-xs uppercase tracking-wider text-donkey-grey mb-2">Weakest factors to shore up first</p>
            <ul className="space-y-1 mb-5">
              {weakest.map((f) => <li key={f.key} className="text-sm text-donkey-warm/85">• {f.label} — <span className="text-donkey-grey">{OPTS[vals[f.key]]}</span></li>)}
            </ul>
            <div className="rounded-xl bg-donkey-orange/10 border border-donkey-orange/30 p-4">
              <p className="text-sm text-donkey-warm mb-3">Want the version backed by real market evidence, not just your own view? Start with Prove — £29, including your complete Donkey's Verdict and first month of validation.</p>
              <Link to="/pricing"><Button className="bg-donkey-orange text-donkey-black font-semibold" data-testid="viability-cta">Start with Prove — £29</Button></Link>
            </div>
            <p className="text-[11px] text-donkey-grey mt-3">Honest note: no guarantees, and this score can't see your market. It only mirrors your answers.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
